import { IPhoto, PhotosDialog, Ratings, ReportService, Review } from '@geovelo-frontends/commons';
import {
  AccountCircle as AccountIcon,
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';

interface IProps {
  openReviewDialog: (open: boolean) => void;
}

function Details({ openReviewDialog }: IProps): React.JSX.Element {
  const {
    user: { current: currentUser },
    report: { selectedReport },
    actions: { setSelectedReport, openSignInDialog },
  } = useContext(AppContext);
  const [showedPhotos, showPhotos] = useState<IPhoto[] | null>(null);
  const [showedPhotoIndex, setShowedPhotoIndex] = useState(0);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  async function handleRating(rating: Ratings): Promise<void> {
    if (!selectedReport) return;

    if (!currentUser) {
      openSignInDialog(true);
      return;
    }

    let review: Review | null | undefined;
    if (currentUser.username) {
      review =
        rating === Ratings.Useful
          ? selectedReport.getSignedInUserUsefulReview(currentUser.id)
          : selectedReport.getSignedInUserUnusefulReview(currentUser.id);
    }
    if (review) {
      try {
        await ReportService.deleteReview(review.id);

        const index = selectedReport.reviews.findIndex(({ id }) => id === review?.id);
        selectedReport.reviews.splice(index, 1);
        setSelectedReport(selectedReport.clone());
      } catch {
        enqueueSnackbar(t('commons.report.reviews.not_added'));
      }
    } else {
      try {
        const newReview = await ReportService.addReview({
          reportId: selectedReport.id,
          comment: '',
          rating,
        });
        selectedReport.reviews.push(newReview);

        if (currentUser.username) {
          review =
            rating === Ratings.NotUseful
              ? selectedReport.getSignedInUserUsefulReview(currentUser.id)
              : selectedReport.getSignedInUserUnusefulReview(currentUser.id);
        }

        if (review) {
          const index = selectedReport.reviews.findIndex(({ id }) => id === review?.id);
          selectedReport.reviews.splice(index, 1);
        }

        setSelectedReport(selectedReport.clone());
      } catch {
        enqueueSnackbar(t('commons.report.reviews.not_added'));
      }
    }
  }

  function ratingIconColor(isRatingPositive: boolean): 'primary' | 'error' | undefined {
    if (!currentUser || !currentUser.username || !selectedReport) return undefined;

    if (isRatingPositive) {
      const review = selectedReport.getSignedInUserUsefulReview(currentUser.id);
      return review ? 'primary' : undefined;
    } else {
      const review = selectedReport.getSignedInUserUnusefulReview(currentUser.id);
      return review ? 'error' : undefined;
    }
  }

  return (
    <div>
      {selectedReport && (
        <>
          <Card sx={{ margin: 2 }}>
            {selectedReport.photos[0] && (
              <img
                alt="Report photo"
                src={selectedReport.photos[0].url}
                style={{ width: '100%' }}
              />
            )}
            <CardContent>
              <Typography variant="body1">{selectedReport.description}</Typography>
              <Typography display="block" textAlign="end" variant="caption">
                <Trans
                  i18nKey="bicycle_facilities.reports.list.report_subtitle"
                  values={{
                    created: moment(selectedReport.created).format('LLL'),
                    creator: selectedReport.creator || '',
                  }}
                />
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button
                color={ratingIconColor(true)}
                onClick={() => handleRating(Ratings.Useful)}
                startIcon={<ThumbUpIcon />}
              >
                ({selectedReport.nbUsefulRatings})
              </Button>
              <Button
                color={ratingIconColor(false)}
                onClick={() => handleRating(Ratings.NotUseful)}
                startIcon={<ThumbDownIcon />}
              >
                ({selectedReport.nbNotUsefulRatings})
              </Button>
            </CardActions>
          </Card>
          <Box margin={3}>
            <Box
              alignItems="center"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={3}
            >
              <Typography color="textSecondary" variant="body1">
                <Trans
                  count={selectedReport.nbReviews}
                  i18nKey="commons.report.reviews.title"
                  values={{ count: selectedReport.nbReviews }}
                />
              </Typography>
              {currentUser && (
                <Button color="primary" onClick={() => openReviewDialog(true)} variant="contained">
                  <Trans i18nKey="commons.actions.comment" />
                </Button>
              )}
            </Box>
            {!currentUser && (
              <Box display="flex" flexDirection="row">
                <Typography variant="body2">
                  <Trans i18nKey="commons.report.reviews.not_signedin" />
                </Typography>
                <Button
                  color="primary"
                  onClick={() => openSignInDialog(true)}
                  sx={{ marginLeft: 2, minWidth: 150 }}
                  variant="contained"
                >
                  <Trans i18nKey="commons.actions.sign_in" />
                </Button>
              </Box>
            )}
            {selectedReport.reviews
              .filter(({ comment, photos }) => comment || photos.length > 0)
              .map((review) => (
                <Box display="flex" flexDirection="row" gap={2} key={review.id} marginTop={2}>
                  {review.creator ? (
                    <Tooltip title={review.creator.username}>
                      <AccountIcon color="secondary" sx={{ flexShrink: 0 }} />
                    </Tooltip>
                  ) : (
                    <AccountIcon color="secondary" sx={{ flexShrink: 0 }} />
                  )}
                  <Card sx={{ flexGrow: 1 }}>
                    <CardContent>
                      {review.comment && <Typography variant="body2">{review.comment}</Typography>}
                      <Typography variant="caption">
                        {moment(review.created).format('LLL')}
                      </Typography>
                    </CardContent>
                    {review.photos.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={1} padding={2} paddingTop={0}>
                        {review.photos.map((photo, index) => (
                          <Box flexShrink={0} key={photo.id} width={100}>
                            <CardActionArea
                              onClick={() => {
                                setShowedPhotoIndex(index);
                                showPhotos(review.photos.map(({ url }) => ({ url })));
                              }}
                            >
                              <CardMedia component="img" height={80} image={photo.url} />
                            </CardActionArea>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Card>
                </Box>
              ))}
          </Box>
        </>
      )}
      <PhotosDialog
        initialIndex={showedPhotoIndex}
        onClose={() => showPhotos(null)}
        open={Boolean(showedPhotos)}
        photos={showedPhotos}
      />
    </div>
  );
}

export default Details;
